import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    UploadCloud,
    CheckCircle2,
    Trash2,
    FileText,
    Calendar,
    BookOpen,
    Send,
    AlertCircle,
    MessageSquare,
} from "lucide-react";
import { BASE_URL, mixContentQuestionsApi } from "../../../../api/api";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { token } from "../../../../api/auth";

const InterviewGuidance = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({}); // {questionId: File}
    const DRAFT_KEY = "interviewGuidanceDraft";

    // Fetch questions
    useEffect(() => {
        axios
            .get(`${BASE_URL}${mixContentQuestionsApi}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                if (res.data?.data) setQuestions(res.data.data);
                else setQuestions([]);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching questions:", err);
                toast.error(
                    "Failed to load questions. Please try again later.",
                );
                setLoading(false);
            });
    }, []);

    // Load saved draft from localStorage
    useEffect(() => {
        const savedDraft = localStorage.getItem(DRAFT_KEY);
        if (savedDraft) {
            const parsed = JSON.parse(savedDraft);
            const restoredAnswers = {};
            Object.entries(parsed).forEach(([qid, meta]) => {
                restoredAnswers[qid] = meta;
            });
            setAnswers(restoredAnswers);

            const answeredIds = Object.keys(parsed).map((id) => Number(id));
            if (answeredIds.length > 0 && questions.length > 0) {
                const lastId = Math.max(...answeredIds);
                const lastIndex = questions.findIndex((q) => q.id === lastId);
                if (lastIndex !== -1) setCurrentIndex(lastIndex);
            }
        }
    }, [questions]);

    // Handle file uploads
    const handleAnswerChange = (e, qId) => {
        const file = e.target.files[0];
        if (!file) return;

        setAnswers((prev) => {
            const updated = { ...prev, [qId]: file };
            // Save metadata to localStorage
            const metadata = {};
            Object.entries(updated).forEach(([id, f]) => {
                metadata[id] = {
                    name: f.name,
                    type: f.type,
                    lastModified: f.lastModified,
                };
            });
            localStorage.setItem(DRAFT_KEY, JSON.stringify(metadata));
            return updated;
        });
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
    };

    const handlePrev = () => {
        if (currentIndex > 0) setCurrentIndex((i) => i - 1);
    };

    const handleClearDraft = () => {
        localStorage.removeItem(DRAFT_KEY);
        setAnswers({});
        toast.info("Draft cleared successfully.");
    };

    const handleSubmit = async () => {
        const studentSession = sessionStorage.getItem("studentSession");
        if (!studentSession) {
            toast.warning("Please log in to submit your answers.", {
                onClose: () => (window.location.href = "/login"),
            });
            return;
        }

        const studentData = JSON.parse(studentSession);
        const studentId = studentData?.id;
        if (!studentId) {
            toast.error("Student ID not found. Please log in again.");
            return;
        }

        const submitPromises = questions
            .map((q) => {
                const file = answers[q.id];
                if (!file || !(file instanceof File)) return null;

                const formData = new FormData();
                formData.append("question_id", q.id);
                formData.append("student_id", studentId);
                formData.append("answer", file);

                return axios.post(
                    `${BASE_URL}${mixContentQuestionsApi}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
            })
            .filter(Boolean);

        if (submitPromises.length === 0) {
            toast.warning(
                "Please upload at least one valid new file before submitting. (Restored drafts must be re-uploaded)",
            );
            return;
        }

        try {
            await Promise.all(submitPromises);
            toast.success("All your answers have been submitted successfully!");
            setAnswers({});
            localStorage.removeItem(DRAFT_KEY);
            setCurrentIndex(0);
        } catch (err) {
            console.error("Error submitting answers:", err);
            toast.error(
                "Failed to submit some answers. Please try again later.",
            );
        }
    };

    // Animation variants matching established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    if (!loading && questions.length === 0) {
        return null;
    }

    const currentQuestion = questions[currentIndex];
    const currentAnswer = currentQuestion ? answers[currentQuestion.id] : null;
    const isDraftOnly = currentAnswer && !(currentAnswer instanceof File);

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Standardized Editorial Header */}
                <div className="mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                            <span className="w-6 h-1 bg-red-700"></span>
                            UPSC & PSC Practice Panel
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4 ">
                            Interview{" "}
                            <span className="text-yellow-500">Guidance.</span>
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed max-w-2xl">
                            Sharpen your communication skills. Review potential
                            questions, prepare your responses, and upload your
                            documents for expert feedback.
                        </p>
                    </motion.div>
                </div>

                {loading ? (
                    <div className="w-full h-96 bg-white rounded-xl border border-gray-200 animate-pulse"></div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Left Column: Active Question Panel */}
                        <motion.div
                            className="lg:col-span-8 flex flex-col"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestion.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: "easeOut",
                                    }}
                                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 flex flex-col flex-grow"
                                >
                                    {/* Tags */}
                                    <div className="flex flex-wrap items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                                        <span className="inline-flex items-center px-3 py-1 bg-neutral-900 text-white rounded font-bold text-xs tracking-widest uppercase shadow-sm">
                                            <Calendar
                                                size={12}
                                                className="mr-1.5"
                                            />
                                            {currentQuestion.year}
                                        </span>
                                        <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-neutral-700 rounded font-bold text-xs tracking-widest uppercase border border-gray-200">
                                            <BookOpen
                                                size={12}
                                                className="mr-1.5 text-red-700"
                                            />
                                            {currentQuestion.subject}
                                        </span>
                                        <span className="ml-auto text-sm font-bold text-gray-400">
                                            Q {currentIndex + 1} of{" "}
                                            {questions.length}
                                        </span>
                                    </div>

                                    {/* Question Text */}
                                    <div className="flex items-start mb-10">
                                        <MessageSquare
                                            className="text-yellow-500 mr-4 mt-1 flex-shrink-0"
                                            size={24}
                                        />
                                        <div
                                            className="prose prose-lg max-w-none text-neutral-900 font-medium leading-relaxed"
                                            dangerouslySetInnerHTML={{
                                                __html: currentQuestion.question,
                                            }}
                                        />
                                    </div>

                                    {/* Upload Area */}
                                    <div className="mt-auto pt-6 border-t border-gray-100">
                                        <h4 className="text-sm font-bold text-neutral-900 mb-4 flex items-center">
                                            <UploadCloud
                                                size={16}
                                                className="mr-2 text-yellow-500"
                                            />
                                            Upload Your Response
                                        </h4>

                                        <label
                                            className={`relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 group ${currentAnswer ? "border-green-400 bg-green-50" : "border-gray-300 bg-gray-50 hover:bg-yellow-50/50 hover:border-yellow-400"}`}
                                        >
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) =>
                                                    handleAnswerChange(
                                                        e,
                                                        currentQuestion.id,
                                                    )
                                                }
                                            />

                                            {currentAnswer ? (
                                                <div className="text-center px-4">
                                                    <CheckCircle2
                                                        size={32}
                                                        className="mx-auto text-green-500 mb-3"
                                                    />
                                                    <p className="text-sm font-bold text-green-800 line-clamp-1 mb-1">
                                                        {currentAnswer.name}
                                                    </p>
                                                    {isDraftOnly ? (
                                                        <p className="text-xs text-red-600 font-bold flex items-center justify-center mt-2">
                                                            <AlertCircle
                                                                size={12}
                                                                className="mr-1"
                                                            />{" "}
                                                            Re-upload required
                                                            for submission
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-green-600 font-medium">
                                                            Ready to submit.
                                                            Click to change
                                                            file.
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200 mx-auto mb-3 group-hover:scale-110 group-hover:border-yellow-400 transition-all">
                                                        <UploadCloud
                                                            className="text-gray-400 group-hover:text-yellow-500"
                                                            size={20}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-bold text-neutral-700 block mb-1">
                                                        Click to browse or drag
                                                        file here
                                                    </span>
                                                    <span className="text-xs text-gray-500 font-medium">
                                                        PDF, JPG, or PNG (Max
                                                        10MB)
                                                    </span>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </motion.div>
                            </AnimatePresence>

                            {/* Navigation Controls */}
                            <div className="flex items-center justify-between mt-6 gap-4">
                                <button
                                    onClick={handlePrev}
                                    disabled={currentIndex === 0}
                                    className={`flex items-center px-6 py-3 rounded-lg font-bold text-sm transition-all duration-300 ${currentIndex === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white border border-gray-200 text-neutral-900 hover:border-neutral-900 hover:shadow-sm"}`}
                                >
                                    <ChevronLeft size={18} className="mr-1" />{" "}
                                    Previous
                                </button>

                                {currentIndex < questions.length - 1 ? (
                                    <button
                                        onClick={handleNext}
                                        className="flex items-center px-8 py-3 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                                    >
                                        Next Question{" "}
                                        <ChevronRight
                                            size={18}
                                            className="ml-1 group-hover:translate-x-1 transition-transform"
                                        />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleSubmit}
                                        className="flex items-center px-8 py-3 bg-red-700 hover:bg-neutral-900 text-white font-bold rounded-lg shadow-md transition-colors duration-300 group"
                                    >
                                        Submit Files{" "}
                                        <Send
                                            size={16}
                                            className="ml-2 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                                        />
                                    </button>
                                )}
                            </div>
                        </motion.div>

                        {/* Right Column: Sidebar */}
                        <motion.div
                            className="lg:col-span-4 flex flex-col gap-6"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* Progress Tracker */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h3 className="text-sm font-bold text-neutral-900 mb-4 uppercase tracking-widest border-b border-gray-100 pb-3">
                                    Session Progress
                                </h3>

                                <div className="flex flex-wrap gap-2">
                                    {questions.map((q, idx) => {
                                        const isAnswered = !!answers[q.id];
                                        const isCurrent = currentIndex === idx;
                                        return (
                                            <button
                                                key={q.id}
                                                onClick={() =>
                                                    setCurrentIndex(idx)
                                                }
                                                className={`w-10 h-10 rounded-lg text-sm font-bold flex items-center justify-center transition-all duration-300 ${
                                                    isCurrent
                                                        ? "bg-neutral-900 text-white shadow-md"
                                                        : isAnswered
                                                          ? "bg-green-100 text-green-700 border border-green-200"
                                                          : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-neutral-900 hover:text-neutral-900"
                                                }`}
                                            >
                                                {idx + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Draft List */}
                            {Object.keys(answers).length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 p-0 shadow-sm overflow-hidden flex flex-col max-h-[400px]">
                                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                        <h3 className="text-sm font-bold text-neutral-900 uppercase tracking-widest">
                                            Saved Drafts
                                        </h3>
                                        <span className="bg-yellow-400 text-neutral-900 text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest">
                                            {Object.keys(answers).length}
                                        </span>
                                    </div>

                                    <div className="overflow-y-auto p-3 flex-1 custom-scrollbar">
                                        <ul className="space-y-2">
                                            {Object.entries(answers).map(
                                                ([qid, file]) => {
                                                    const qIndex =
                                                        questions.findIndex(
                                                            (q) =>
                                                                q.id ===
                                                                Number(qid),
                                                        );
                                                    const isDraftMetadata = !(
                                                        file instanceof File
                                                    );

                                                    return (
                                                        <li
                                                            key={qid}
                                                            onClick={() =>
                                                                qIndex !== -1 &&
                                                                setCurrentIndex(
                                                                    qIndex,
                                                                )
                                                            }
                                                            className="p-3 rounded-lg border border-gray-100 hover:border-yellow-400 hover:bg-yellow-50/30 cursor-pointer transition-colors group flex items-start gap-3"
                                                        >
                                                            <FileText
                                                                size={16}
                                                                className={`mt-0.5 ${isDraftMetadata ? "text-red-400" : "text-green-500"}`}
                                                            />
                                                            <div className="flex-1 overflow-hidden">
                                                                <p className="text-xs font-bold text-neutral-900 mb-0.5">
                                                                    Question{" "}
                                                                    {qIndex + 1}
                                                                </p>
                                                                <p className="text-[11px] text-gray-500 truncate">
                                                                    {file?.name ||
                                                                        "Draft saved"}
                                                                </p>
                                                            </div>
                                                        </li>
                                                    );
                                                },
                                            )}
                                        </ul>
                                    </div>

                                    <div className="p-4 border-t border-gray-100 bg-gray-50 mt-auto">
                                        <button
                                            onClick={handleClearDraft}
                                            className="w-full flex items-center justify-center px-4 py-2 bg-white border border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-200 rounded font-bold text-xs uppercase tracking-widest transition-colors duration-300"
                                        >
                                            <Trash2
                                                size={14}
                                                className="mr-2"
                                            />{" "}
                                            Clear All Drafts
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default InterviewGuidance;
